import { ref, computed, watch } from 'vue'
import { maskPhone, isGroupJid, stripJidSuffix } from '../lib/phoneMask'

/**
 * Phone privacy toggle — supports BOTH legacy per-user opt-in
 * (localStorage) and PR #88 RBAC team-driven enforcement.
 *
 * Resolution order:
 *   1. If a team the user belongs to has `mask_contact_phones=true`,
 *      backend returns `effectiveMaskContactPhones=true` on /auth/me.
 *      The UI then FORCES masking on and disables the local toggle —
 *      the user cannot opt out of an admin policy.
 *   2. Otherwise the user's localStorage choice wins (default OFF for
 *      backward compatibility with pre-PR #87 deployments).
 *
 * The composable exposes a module-level singleton so toggling once
 * propagates instantly across every view. Tests reset state via
 * `setEnabled(false)` plus `setTeamForcedSource(() => false)` — keeping
 * the team source as an injectable getter avoids dragging Pinia into
 * unit tests that only care about the masking logic.
 */

const STORAGE_KEY = 'neogo_mask_contact_phones'

function readInitial(): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    // Safari private mode etc. — fail closed (no masking).
    return false
  }
}

// Per-user opt-in (localStorage). Module-level singleton so toggling in
// one component reflects everywhere immediately.
const localEnabled = ref<boolean>(readInitial())

// Team-forced source — a getter so the caller (App.vue bootstrap) can
// wire it to Pinia without us importing the store here. Default is a
// constant `false` so tests / standalone usage don't need any wiring.
let teamForcedSource: () => boolean = () => false

/**
 * Wire the team-driven masking flag. Pass a getter that returns the
 * current value of `userStore.user?.effectiveMaskContactPhones`.
 * Call once at app bootstrap (App.vue / main.ts) after the user store
 * is hydrated. Subsequent /auth/me refreshes just mutate the store —
 * the getter sees the new value on next access (reactive via Vue
 * tracking when invoked inside a computed/watch).
 */
export function setTeamForcedSource(getter: () => boolean): void {
  teamForcedSource = getter
}

// Persist local choice on change. Using `watch` (not `watchEffect`) so
// we don't write to localStorage during the initial read.
watch(localEnabled, (v) => {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, v ? '1' : '0')
  } catch {
    // Storage quota / private mode — silently ignore. The in-memory
    // state still wins for the current session.
  }
})

export function usePhonePrivacy() {
  // Reactive: re-runs whenever the team source changes (e.g. /auth/me
  // refetch updates userStore.user) or the local toggle flips.
  const teamForced = computed(() => teamForcedSource())
  const enabled = computed(() => teamForced.value || localEnabled.value)

  function toggle(): void {
    // Refuse to flip when the team policy forces masking on — the
    // admin's decision wins. The UI mirrors this by disabling the
    // toggle, but we guard here too in case someone calls the
    // composable directly.
    if (teamForced.value) return
    localEnabled.value = !localEnabled.value
  }

  function setEnabled(v: boolean): void {
    if (teamForced.value) return
    localEnabled.value = v
  }

  /**
   * Render a phone / JID for display in the UI.
   *
   *  - When masking is OFF (default), returns the cleaned local part of
   *    a JID — same shape the rest of the UI has shown for years.
   *  - When masking is ON, returns the `<first4>****<last4>` form.
   *  - Group / broadcast / newsletter JIDs are returned as the
   *    caller-supplied `fallback` (typically the group name); they are
   *    never personal phone numbers and don't need masking.
   *  - Empty / null / undefined returns `fallback`.
   *
   * Reactivity: the template re-runs `display()` whenever `enabled`
   * flips because the function reads `enabled.value` on every call.
   */
  function display(phoneOrJid: string | null | undefined, fallback = ''): string {
    if (!phoneOrJid) return fallback
    if (isGroupJid(phoneOrJid)) return fallback || phoneOrJid
    if (enabled.value) {
      const masked = maskPhone(phoneOrJid)
      return masked || fallback
    }
    return stripJidSuffix(phoneOrJid)
  }

  return {
    enabled,
    teamForced,
    toggle,
    setEnabled,
    display,
  }
}
